import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet } from '../utils/api';

type PlatformIdentityListItem = {
  platformIdentity: {
    id: string;
    shortId: string;
    status: 'ACTIVE' | 'BLOCKED' | 'ARCHIVED' | string;
    createdAt: string;
    updatedAt: string;
  };
  personLabel: string;
  telegram: {
    accountsCount: number;
    hasMultipleAccounts: boolean;
    primary: {
      telegramChatId: string;
      telegramUsername?: string | null;
      isPrimary: boolean;
    } | null;
  };
  tennis: {
    exists: boolean;
    playerId: string | null;
    playerName: string | null;
    playerEmail: string | null;
    playerActive: boolean | null;
  };
  padel: {
    exists: boolean;
    padelProfileId: string | null;
    status: string | null;
  };
};

type PlatformIdentityListResponse = {
  items: PlatformIdentityListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
};

const statusBadge = (status: string): string => {
  if (status === 'ACTIVE') return 'bg-green-100 text-green-800';
  if (status === 'BLOCKED') return 'bg-red-100 text-red-800';
  if (status === 'ARCHIVED') return 'bg-gray-100 text-gray-700';
  return 'bg-gray-100 text-gray-700';
};

const shortMaskedChat = (chatId?: string | null): string => {
  const value = String(chatId || '').trim();
  if (!value) return '—';
  return `***${value.slice(-4)}`;
};

const formatDateTimeRu = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const PlatformIdentities: React.FC = () => {
  const [items, setItems] = useState<PlatformIdentityListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    profile: 'all',
    sort: 'name_asc',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 40,
    total: 0,
    pages: 0,
  });

  const fetchIdentities = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiGet<PlatformIdentityListResponse>('/platform-identities', {
        search: filters.search,
        status: filters.status,
        profile: filters.profile,
        sort: filters.sort,
        page: pagination.page,
        limit: pagination.limit,
      });
      setItems(data.items || []);
      setPagination((prev) => ({
        ...prev,
        ...(data.pagination || prev),
      }));
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить идентичности');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIdentities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.profile, filters.sort, pagination.page, pagination.limit]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination((prev) => ({ ...prev, page: 1 }));
      fetchIdentities();
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  const totalLabel = useMemo(() => `${pagination.total} записей`, [pagination.total]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Идентичности</h1>
        <button
          type="button"
          onClick={fetchIdentities}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          Обновить
        </button>
      </div>

      <div className="bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Поиск</label>
            <input
              type="text"
              value={filters.search}
              onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
              placeholder="ID, игрок, email, Telegram"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Статус</label>
            <select
              value={filters.status}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, status: event.target.value }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Все</option>
              <option value="ACTIVE">ACTIVE</option>
              <option value="BLOCKED">BLOCKED</option>
              <option value="ARCHIVED">ARCHIVED</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Профиль</label>
            <select
              value={filters.profile}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, profile: event.target.value }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Все</option>
              <option value="tennis">Tennis</option>
              <option value="padel">Padel</option>
              <option value="no-tennis">Без Tennis</option>
              <option value="no-padel">Без Padel</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Сортировка</label>
            <select
              value={filters.sort}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, sort: event.target.value }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="name_asc">По имени (А-Я)</option>
              <option value="name_desc">По имени (Я-А)</option>
              <option value="created_desc">Создана (новые)</option>
              <option value="created_asc">Создана (старые)</option>
              <option value="updated_desc">Обновлена (новые)</option>
              <option value="updated_asc">Обновлена (старые)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">На странице</label>
            <select
              value={pagination.limit}
              onChange={(event) =>
                setPagination((prev) => ({
                  ...prev,
                  page: 1,
                  limit: Number(event.target.value) || 40,
                }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={30}>30</option>
              <option value={40}>40</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Загрузка идентичностей...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Идентичности не найдены</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Человек</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Статус</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telegram</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tennis</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Padel</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Создана</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item) => (
                  <tr key={item.platformIdentity.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <Link to={`/identities/${item.platformIdentity.id}`} className="font-medium text-blue-700 hover:text-blue-900">
                        {item.personLabel}
                      </Link>
                      <div className="text-xs text-gray-500 mt-1">
                        Identity: {item.platformIdentity.shortId}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge(item.platformIdentity.status)}`}>
                        {item.platformIdentity.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {item.telegram.accountsCount === 0 ? (
                        <span>—</span>
                      ) : item.telegram.accountsCount === 1 ? (
                        <div>
                          <div>{item.telegram.primary?.telegramUsername ? `@${item.telegram.primary.telegramUsername}` : shortMaskedChat(item.telegram.primary?.telegramChatId)}</div>
                          <div className="text-xs text-gray-500">{shortMaskedChat(item.telegram.primary?.telegramChatId)}</div>
                        </div>
                      ) : (
                        <div>
                          <div>{item.telegram.accountsCount} аккаунта</div>
                          <div className="text-xs text-gray-500">
                            {item.telegram.primary?.isPrimary ? 'есть основной' : 'без primary'}
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {item.tennis.exists ? (
                        <div>
                          <div className="text-xs text-gray-500">{item.tennis.playerActive ? 'Активен' : 'Неактивен'}</div>
                          {item.tennis.playerId && (
                            <Link className="text-xs text-blue-700 hover:text-blue-900 underline" to={`/players?search=${encodeURIComponent(item.tennis.playerId)}`}>
                              Открыть игрока
                            </Link>
                          )}
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {item.padel.exists ? (
                        <div>
                          <div className="font-medium">Padel ✓</div>
                          <div className="text-xs text-gray-500">{item.padel.status || '—'}</div>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatDateTimeRu(item.platformIdentity.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex justify-between items-center text-sm text-gray-600">
        <span>{totalLabel}</span>
        {pagination.pages > 1 && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={pagination.page <= 1}
              className="px-3 py-2 border border-gray-300 rounded-md bg-white disabled:opacity-50"
            >
              Назад
            </button>
            <span>
              {pagination.page} / {pagination.pages}
            </span>
            <button
              type="button"
              onClick={() =>
                setPagination((prev) => ({ ...prev, page: Math.min(prev.pages || 1, prev.page + 1) }))
              }
              disabled={pagination.page >= pagination.pages}
              className="px-3 py-2 border border-gray-300 rounded-md bg-white disabled:opacity-50"
            >
              Вперёд
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformIdentities;
